import * as d3 from 'd3';
import { Component, h } from 'preact';

import find from '../utils/find';

import { DEMOCRAT, REPUBLICAN } from '../colors';

class MiniBalancePower extends Component {
  constructor() {
    super();

    this.state = {
      width: null,
      height: 30,
      bodyWidth: null,
      breakPoint: 544,
    };

    this.createGraphic = this.createGraphic.bind(this);
    this.redrawGraphic = this.redrawGraphic.bind(this);
    this.prepareData = this.prepareData.bind(this);
    this.getContainerWidth = this.getContainerWidth.bind(this);
  }

  componentDidMount() {
    window.addEventListener('resize', () => {
      this.redrawGraphic();
    });
  }

  prepareData(data, className, chamber, total, halfway) {
    let republican = 0,
      democrat = 0,
      vacant = 0,
      uncontested_republican = 0,
      uncontested_democrat = 0;

    data.forEach((contest) => {
      if (
        contest.contest_result == 'not called' &&
        contest.candidacies.length > 1
      ) {
        vacant++;
      } else if (
        (contest.contest_result == 'not called' ||
          contest.contest_result == 'winner') &&
        contest.candidacies.length == 1 &&
        contest.candidacies[0].party == 'Republican'
      ) {
        uncontested_republican++;
      } else if (
        (contest.contest_result == 'not called' ||
          contest.contest_result == 'winner') &&
        contest.candidacies.length == 1 &&
        contest.candidacies[0].party == 'Democrat'
      ) {
        uncontested_democrat++;
      } else if (
        contest.contest_result == 'winner' &&
        contest.candidacies.length > 1 &&
        find(contest.candidacies, (candidate) => candidate.is_winner === true)
          .party == 'Republican'
      ) {
        republican++;
      } else if (
        contest.contest_result == 'winner' &&
        contest.candidacies.length > 1 &&
        find(contest.candidacies, (candidate) => candidate.is_winner === true)
          .party == 'Democrat'
      ) {
        democrat++;
      }
    });

    return {
      chamber: chamber,
      className: className,
      republican: republican,
      democrat: democrat,
      vacant: vacant,
      halfway: halfway,
      total: total,
      uncontested_republican: uncontested_republican,
      uncontested_democrat: uncontested_democrat,
    };
  }

  createGraphic(className, currentData) {
    let { width, bodyWidth } = this.getContainerWidth(className);

    let { height, breakPoint } = this.state;

    let currentElection = [
      {
        start:
          currentData.republican +
          currentData.uncontested_republican +
          currentData.vacant,
        end:
          currentData.republican +
          currentData.uncontested_republican +
          currentData.vacant +
          currentData.democrat +
          currentData.uncontested_democrat,
        color: DEMOCRAT,
        party: 'Democrats',
        seats: currentData.democrat + currentData.uncontested_democrat,
      },
      {
        start: currentData.republican + currentData.uncontested_republican,
        end:
          currentData.republican +
          currentData.uncontested_republican +
          currentData.vacant,
        color: '#bdbdbd',
        party: 'Up for Grabs',
        seats: currentData.vacant,
      },
      {
        start: 0,
        end: currentData.republican + currentData.uncontested_republican,
        color: REPUBLICAN,
        party: 'Republicans',
        seats: currentData.republican + currentData.uncontested_republican,
      },
    ];

    d3.selectAll(`.${className}-graphic-mini svg`).remove();

    // chart setup
    let margin = { top: 5, bottom: 0, right: 0, left: 0 };

    (width = width - margin.left - margin.right),
      (height = height - margin.top - margin.bottom);

    const isMobile = bodyWidth < breakPoint;

    let svg = d3
      .select(`.${className}-graphic-mini`)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom);

    let diagonalStripePattern = svg
      .append('defs')
      .append('pattern')
      .attr('id', 'diagonal-stripe')
      .attr('patternUnits', 'userSpaceOnUse')
      .attr('patternTransform', 'rotate(45 0 0)')
      .attr('width', 6)
      .attr('height', 6);

    diagonalStripePattern
      .append('rect')
      .attr('width', 6)
      .attr('height', 6)
      .style('fill', '#d9d9d9');

    diagonalStripePattern
      .append('line')
      .attr('x1', 0)
      .attr('y1', 0)
      .attr('x2', 0)
      .attr('y2', 6)
      .style('stroke', 'black')
      .style('stroke-width', 0.5);

    let g = svg
      .append('g')
      .attr('class', `${className}-graphic-mini-group`)
      .attr('width', width)
      .attr('transform', `translate(${margin.left},${margin.top})`);

    let xCurrent = d3
      .scaleLinear()
      .domain([0, currentData.total])
      .range([0, width]);

    let currentBar = g
      .selectAll('g')
      .data(currentElection)
      .enter()
      .append('g')
      .attr('transform', 'translate(0,' + 5 + ')');

    currentBar
      .append('rect')
      .attr('class', 'current-bar')
      .attr('width', (d) => xCurrent(d.end - d.start))
      .attr('x', (d) => xCurrent(d.start))
      .attr('fill', (d) => d.color)
      .attr('height', 10);

    if (className != 'us-house') {
      g.append('line')
        .attr('x1', xCurrent(currentData.halfway))
        .attr('x2', xCurrent(currentData.halfway))
        .attr('y1', 0)
        .attr('y2', height)
        .attr('class', 'annotation-line');
    }
  }

  getContainerWidth(className) {
    let base;

    const body = d3.select('body');

    base = d3.select(`.mini-${className}`);

    const width = base.node().offsetWidth;
    const bodyWidth = body.node().offsetWidth;

    return { width, bodyWidth };
  }

  redrawGraphic() {
    const { className, currentData } = this.props;

    d3.selectAll(`.${className}-graphic-mini svg`).remove();

    let { width, bodyWidth } = this.getContainerWidth(className);

    this.setState({ width: width, bodyWidth: bodyWidth });
    this.createGraphic(className, currentData);
  }

  componentWillUnmount() {
    window.addEventListener('resize', () => {
      this.redrawGraphic();
    });
  }

  render() {
    const { className, currentData } = this.props;

    const upForGrabs = (
      <div>
        {currentData.vacant > 0 && (
          <div>
            There {currentData.vacant > 1 ? 'are' : 'is'} {currentData.vacant}{' '}
            seat
            {currentData.vacant > 1 ? 's' : ''} up for grabs.
          </div>
        )}
        <div>
          {currentData.uncontested_republican +
            currentData.uncontested_democrat}{' '}
          seats uncontested.
        </div>
      </div>
    );

    const partisanBreakdown = (
      <div class='mini-balance-of-power-text'>
        <div class='republican-mini-balance'>
          <span class='rep-winner'>
            {currentData.republican + currentData.uncontested_republican} R
          </span>
        </div>
        <div class='democrat-mini-balance'>
          <span class='dem-winner'>
            {currentData.democrat + currentData.uncontested_democrat} D
          </span>
        </div>
      </div>
    );

    const totalSeats = `${
      currentData.uncontested_republican > 0 && currentData.total
    } seats`;

    this.createGraphic(className, currentData);
    return (
      <div class='mini-balance-power-graphic'>
        <div class={`mini-balance-power-up-for-grabs`}>
          {currentData.uncontested_republican > 0 && totalSeats}
        </div>
        <div class={`${className}-graphic-mini`}></div>
        {currentData.uncontested_republican > 0 && partisanBreakdown}
        <div class={`mini-balance-power-partisan-breakdown`}>
          {currentData.uncontested_republican > 0 && upForGrabs}
        </div>
      </div>
    );
  }
}

export default MiniBalancePower;
